// src/app/time-attendance/components/holiday/services/holiday.service.ts

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { environment } from '../../../../../environments/environment';

import {
  ApiPagedResponse,
  ApiResponse,
  CreateHolidayBookedHoursBody,
  DepartmentListItem,
  GetDepartmentsOptions,
  GetYywwOptions,
  HolidayDepartmentOption,
  HolidayWeekOption,
  YywwListItem,
} from '../models/holiday.model';

@Injectable({ providedIn: 'root' })
export class HolidayService {
  private readonly baseUrl = environment.apiBaseUrl;

  private readonly departmentsUrl = `${this.baseUrl}/api/departments`;
  private readonly yywwsUrl = `${this.baseUrl}/api/yywws`;
  private readonly createHolidayBookedHoursUrl =
    `${this.baseUrl}/api/booked-hours/create-holiday-booked-hours`;

  constructor(private readonly http: HttpClient) {}

  /* =====================================================
     Utilities
  ===================================================== */

  private buildParams(paramsObj: Record<string, unknown>): HttpParams {
    let params = new HttpParams();

    Object.entries(paramsObj).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });

    return params;
  }

  private getRemainingPages<TItem>(
    totalPages: number,
    fetchPage: (pageNumber: number) => Observable<ApiPagedResponse<TItem[]>>,
  ): Observable<ApiPagedResponse<TItem[]>>[] {
    const requests: Observable<ApiPagedResponse<TItem[]>>[] = [];

    for (let pageNumber = 2; pageNumber <= totalPages; pageNumber += 1) {
      requests.push(fetchPage(pageNumber));
    }

    return requests;
  }

  private fetchAllPagedResults<TItem>(
    fetchPage: (pageNumber: number) => Observable<ApiPagedResponse<TItem[]>>,
  ): Observable<TItem[]> {
    return fetchPage(1).pipe(
      switchMap((firstPage) => {
        const remainingRequests = this.getRemainingPages(firstPage.totalPages, fetchPage);

        if (!remainingRequests.length) {
          return of(firstPage.data);
        }

        return forkJoin(remainingRequests).pipe(
          map((remainingPages) => {
            const allItems = [firstPage.data, ...remainingPages.map((page) => page.data)];
            return allItems.flat();
          }),
        );
      }),
    );
  }

  /* =====================================================
     Create Holiday Booked Hours
  ===================================================== */

  createHolidayBookedHours(body: CreateHolidayBookedHoursBody): Observable<void> {
    return this.http
      .post<ApiResponse<unknown>>(this.createHolidayBookedHoursUrl, body)
      .pipe(map(() => void 0));
  }

  /* =====================================================
     Departments
  ===================================================== */

  getDepartments(options?: GetDepartmentsOptions): Observable<ApiPagedResponse<DepartmentListItem[]>> {
    const params = this.buildParams({
      pageNumber: options?.pageNumber,
      pageSize: options?.pageSize,
      searchTerm: options?.searchTerm,
    });

    return this.http.get<ApiPagedResponse<DepartmentListItem[]>>(this.departmentsUrl, { params });
  }

  getAllDepartments(searchTerm?: string, pageSize = 100): Observable<DepartmentListItem[]> {
    return this.fetchAllPagedResults<DepartmentListItem>((pageNumber) =>
      this.getDepartments({
        pageNumber,
        pageSize,
        searchTerm,
      }),
    );
  }

  getAllDepartmentOptions(searchTerm?: string, pageSize = 100): Observable<HolidayDepartmentOption[]> {
    return this.getAllDepartments(searchTerm, pageSize).pipe(
      map((departments) =>
        departments
          .map((department) => ({
            id: department.id,
            name: department.name,
            code: department.code,
            label: `${department.name} (${department.code})`,
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      ),
    );
  }

  /* =====================================================
     YYWWs
  ===================================================== */

  getYywws(options?: GetYywwOptions): Observable<ApiPagedResponse<YywwListItem[]>> {
    const params = this.buildParams({
      pageNumber: options?.pageNumber,
      pageSize: options?.pageSize,
      searchTerm: options?.searchTerm,
    });

    return this.http.get<ApiPagedResponse<YywwListItem[]>>(this.yywwsUrl, { params });
  }

  getAllYywws(searchTerm?: string, pageSize = 100): Observable<YywwListItem[]> {
    return this.fetchAllPagedResults<YywwListItem>((pageNumber) =>
      this.getYywws({
        pageNumber,
        pageSize,
        searchTerm,
      }),
    );
  }

  getYearYywws(year: number, pageSize = 100): Observable<YywwListItem[]> {
    return this.getAllYywws(String(year), pageSize);
  }

  getYearWeekOptions(year: number, pageSize = 100): Observable<HolidayWeekOption[]> {
    return this.getYearYywws(year, pageSize).pipe(
      map((weeks) =>
        weeks.map((week) => ({
          id: week.id,
          fullYearWeek: week.fullYearWeek,
          year: week.year,
          week: week.week,
          startDate: week.startDate,
          endDate: week.endDate,
          label: `${week.fullYearWeek} (${week.startDate} - ${week.endDate})`,
        })),
      ),
    );
  }
}